import { useTranslation } from 'react-i18next';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import {
    mdiChevronRight,
    mdiShieldCheck,
    mdiShieldRemove,
    mdiShieldSync,
    mdiShieldAlert,
} from '@mdi/js';
import { Theme } from '@mui/material/styles';
import { useApolloClient, useQuery } from '@apollo/client/react';

import { useGlobalDrawer } from 'common/hooks/useGlobalDrawer';
import {
    CurrentUserDocument,
    ICertificateValidationStatus,
    ICertificateWithScoringFragment,
    IPortfolioItemProgressFragment,
} from 'graphql/types';
import { ListItem, ListItemSecondaryAction } from 'common/components/ListItem';
import { IGlobalDrawerType } from 'common/types';
import { Icon } from 'common/components/Icon';
import { formatDate } from 'common/utils/formatDate';

interface IProps {
    certificate: ICertificateWithScoringFragment;
    certificatePortfolioItem?: IPortfolioItemProgressFragment | null;
    isExternalCertificate?: boolean;
    offerEventId: string;
    validationStatus?: ICertificateValidationStatus | null;
}

export const CertificateStatusListItem = ({
    certificate,
    certificatePortfolioItem,
    isExternalCertificate,
    offerEventId,
    validationStatus,
}: IProps) => {
    const [translate] = useTranslation();
    const client = useApolloClient();
    const { openGlobalDrawer } = useGlobalDrawer();
    const { data: currentUserData } = useQuery(CurrentUserDocument);

    const currentUser = currentUserData?.currentUser;

    // Check if certificate is expired
    const isExpired =
        !!certificatePortfolioItem?.endDate &&
        new Date(certificatePortfolioItem.endDate) < new Date();

    const endDateDisplay = !!certificatePortfolioItem?.endDate
        ? formatDate(certificatePortfolioItem.endDate, 'dd MMMM yyyy HH:mm')
        : '';

    const getExternalCertificateDisplay = () => {
        if (!certificatePortfolioItem) {
            return {
                title: translate('certificateNotUploaded'),
                icon: mdiShieldAlert,
            };
        }

        switch (validationStatus) {
            case ICertificateValidationStatus.Pending:
                return {
                    title: translate('certificateSubmitted'),
                    subtitle: translate('certificateAwaitingApproval'),
                    icon: mdiShieldSync,
                };
            case ICertificateValidationStatus.Approved:
                return {
                    title: translate('certificateApproved'),
                    subtitle: undefined,
                    icon: mdiShieldCheck,
                };
            case ICertificateValidationStatus.Denied:
                return {
                    title: translate('certificateRejected'),
                    subtitle: undefined,
                    icon: mdiShieldRemove,
                };
            default:
                return {
                    title: translate('certificateReceived'),
                    subtitle: undefined,
                    icon: mdiShieldCheck,
                };
        }
    };

    const handleClick = () => {
        if (!currentUser) return;

        const { id, name } = certificate;

        openGlobalDrawer({
            type: IGlobalDrawerType.ReceivedCertificate,
            itemId: id,
            props: {
                title: name,
                user: currentUser,
                certificateId: id,
                isExternalCertificate,
                onGenerateCertificate: () => {
                    if (!isExternalCertificate) return;

                    // Invalidate offer event to refresh the certificate status list item
                    client.cache.evict({
                        id: client.cache.identify({
                            __typename: 'OfferEvent',
                            id: offerEventId,
                        }),
                    });
                    client.cache.gc();
                },
            },
        });
    };

    if (isExternalCertificate) {
        const { title, subtitle, icon } = getExternalCertificateDisplay();

        return (
            <ListItem
                button
                color="primary"
                sx={(theme: Theme) => ({
                    borderRadius: 1,
                    '&:hover': { bgcolor: theme.palette.primary.hover },
                })}
                onClick={handleClick}
            >
                <Icon path={icon} size="3rem" />

                <Box sx={{ flex: 1, ml: 2 }}>
                    <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                        {title}
                    </Typography>

                    {subtitle && (
                        <Typography variant="body2">{subtitle}</Typography>
                    )}
                </Box>

                <ListItemSecondaryAction sx={{ mr: -2 }}>
                    <Icon color="primary" path={mdiChevronRight} size="3rem" />
                </ListItemSecondaryAction>
            </ListItem>
        );
    }

    return (
        <ListItem
            button
            color="primary"
            sx={(theme: Theme) => ({
                borderRadius: 1,
                '&:hover': { bgcolor: theme.palette.primary.hover },
            })}
            onClick={handleClick}
        >
            <Icon
                path={isExpired ? mdiShieldRemove : mdiShieldCheck}
                size="3rem"
            />

            <Box sx={{ flex: 1, ml: 2 }}>
                <Typography sx={{ fontWeight: 700, lineHeight: 1.2 }}>
                    {translate(
                        isExpired ? 'certificateExpired' : 'certificateReceived'
                    )}
                </Typography>

                {endDateDisplay && (
                    <Typography color="text.secondary" variant="body2">
                        {translate('portfolioEndDate')}: {endDateDisplay}
                    </Typography>
                )}
            </Box>

            <ListItemSecondaryAction sx={{ mr: -2 }}>
                <Icon color="primary" path={mdiChevronRight} size="3rem" />
            </ListItemSecondaryAction>
        </ListItem>
    );
};
